import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Button,
  DateRangePicker,
  Preloader,
  Tabs,
  Select,
  InfoBlock,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { AuthContext } from 'auth';
import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  GET_WORKERS,
  IGetWorkersResponse,
  IWorkShiftsReportExportRequest,
  IWorkShiftsReportExportResponse,
  IWorkShiftsReportRequest,
  IWorkShiftsReportResponse,
  IWorkShiftsSummaryReportExportResponse,
  WORK_SHIFTS_REPORT,
  WORK_SHIFTS_REPORT_EXPORT,
  WORK_SHIFTS_SUMMARY_REPORT_EXPORT,
} from 'schemas';
import { formatDateForServer } from 'utils';

import { WorkShiftChart, WorkShiftDetails, WorkShiftItem } from './components';
import { SummaryBlocks } from './SummaryBlocks';
import { EType } from './types';
import {
  getNormalizeReportData,
  getWorkersOptions,
  getWorkShiftsPeriod,
} from './utils';
import s from './WorkShifts.module.scss';

export const WorkShifts: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [type, setType] = useState<EType>(EType.SHIFTS);
  const today = dayjs();
  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [selectedWorkShiftId, setSelectedWorkShiftId] = useState<number | null>(
    null
  );
  const [detailedWorkShiftId, setDetailedWorkShiftId] = useState<number | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const { data: workersResponse } = useQuery<IGetWorkersResponse>(GET_WORKERS, {
    fetchPolicy: 'cache-and-network',
    skip: !permissions?.has('worker_manage'),
  });

  const [generateReport, { data: reportData, loading, variables }] =
    useLazyQuery<IWorkShiftsReportResponse, IWorkShiftsReportRequest>(
      WORK_SHIFTS_REPORT,
      {
        fetchPolicy: 'no-cache',
        onError: (error) => {
          if (error?.graphQLErrors) {
            const uniqueMessages = [
              ...new Set(error.graphQLErrors.map((err) => err.message)),
            ];
            setError(uniqueMessages[0]);
          } else if (error?.message) {
            setError(error.message);
          }
        },
      }
    );
  const [generateReportFile, { loading: reportFileLoading }] = useLazyQuery<
    IWorkShiftsReportExportResponse,
    IWorkShiftsReportExportRequest
  >(WORK_SHIFTS_REPORT_EXPORT, {
    onCompleted: (response) => {
      if (!response) return;

      open(response.workShiftsReport.url, '_self');
    },
    fetchPolicy: 'no-cache',
  });
  const [generateSummaryReportFile, { loading: summaryReportFileLoading }] =
    useLazyQuery<
      IWorkShiftsSummaryReportExportResponse,
      IWorkShiftsReportExportRequest
    >(WORK_SHIFTS_SUMMARY_REPORT_EXPORT, {
      onCompleted: (response) => {
        if (!response) return;

        open(response.workShiftsSummaryReport.url, '_self');
      },
      onError: (error) => {
        setError(error.message);
      },
      fetchPolicy: 'no-cache',
    });

  const workersOptions = useMemo(
    () => getWorkersOptions(workersResponse),
    [workersResponse]
  );

  const { shifts, summary, total } = useMemo(
    () => getNormalizeReportData(reportData),
    [reportData]
  );

  useEffect(() => {
    generateReport({
      variables: {
        input: {
          from: formatDateForServer(dayjs(startDate)),
          to: formatDateForServer(dayjs(endDate)),
          workerUuid: selectedWorkerId,
        },
      },
    });
  }, []);

  const size = useWindowResize();
  return (
    <div className={s.container}>
      {error && (
        <InfoBlock title={t('errors.error')} message={error} direction="row" />
      )}
      {detailedWorkShiftId && (
        <WorkShiftDetails
          workShiftId={detailedWorkShiftId}
          close={() => setDetailedWorkShiftId(null)}
        />
      )}
      <div className={s.controls}>
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={today.format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />
        <Select
          name="workers-select"
          value={selectedWorkerId}
          onChange={setSelectedWorkerId}
          options={workersOptions}
          placeholder={t('report.workShifts.all')}
          errorWrapperClassName={s.worker}
        />

        <Button
          onClick={() => {
            return generateReport({
              variables: {
                input: {
                  from: formatDateForServer(dayjs(startDate)),
                  to: formatDateForServer(dayjs(endDate)),
                  workerUuid: selectedWorkerId,
                },
              },
            });
          }}
          disabled={loading}
          className={s.generate}
        >
          {t('reports.generate')}
        </Button>
        <Button
          className={s.export_mobile}
          iconName="IncomeIcon"
          variant="outline"
          onClick={() => {
            if (type === EType.SHIFTS) {
              return generateReportFile({ variables });
            }

            generateSummaryReportFile({ variables });
          }}
          disabled={!variables?.input}
          isLoading={reportFileLoading || summaryReportFileLoading}
        >
          {t('export.csv')}
        </Button>
        <div className={s.right}>
          <Tabs
            value={type}
            onChange={setType}
            options={[
              {
                value: EType.SHIFTS,
                title: t('report.workShifts.type.shifts'),
              },
              {
                value: EType.SUMMARY,
                title: t('report.workShifts.type.summary'),
              },
            ]}
            className={s.type}
          />
          <Button
            className={s.export}
            iconName="IncomeIcon"
            variant="outline"
            onClick={() => {
              if (type === EType.SHIFTS) {
                return generateReportFile({ variables });
              }

              generateSummaryReportFile({ variables });
            }}
            disabled={!variables?.input}
            isLoading={reportFileLoading || summaryReportFileLoading}
          >
            {t('export.csv')}
          </Button>
        </div>
      </div>
      <Preloader
        loading={loading}
        hasData={shifts.length > 0}
        noDataPlaceholder={t('report.placeholder')}
      >
        {type === EType.SHIFTS ? (
          <div className={s.data}>
            <div className={s.header}>
              <span>{getWorkShiftsPeriod(shifts)}</span>
            </div>
            {size.width > 960 ? (
              <WorkShiftChart
                workShifts={shifts}
                loading={loading}
                openWorkShiftDetails={setDetailedWorkShiftId}
              />
            ) : (
              <InfoBlock
                className={s.mobile_info}
                title={t('mobile.placeholder')}
                iconName="ComputerIcon"
                color="blue"
              />
            )}
            {shifts.map((workShift) => (
              <WorkShiftItem
                key={`work-shift-${workShift.id}`}
                workShift={workShift}
                selectWorkShift={setSelectedWorkShiftId}
                openWorkShiftDetails={setDetailedWorkShiftId}
                isSelected={selectedWorkShiftId === workShift.id}
              />
            ))}
          </div>
        ) : (
          <SummaryBlocks total={total} summary={summary} />
        )}
      </Preloader>
    </div>
  );
};
